import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Clock } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { POSTS, getPost } from "@/data/posts";
import { scrollToId } from "@/lib/api";

export default function BlogPost() {
    const { slug } = useParams();
    const post = getPost(slug);

    if (!post) {
        return (
            <div className="container-x flex min-h-[70vh] flex-col items-center justify-center gap-5 pt-24 text-center" data-testid="blog-post-not-found">
                <p className="eyebrow text-gold">404</p>
                <h1 className="font-display text-4xl font-extrabold text-bone">That post doesn't exist.</h1>
                <Link to="/blog" data-testid="blog-back-link" className="mt-4 rounded-full bg-leaf px-8 py-4 font-bold text-ink hover:bg-leaf-bright">
                    Back to the Journal
                </Link>
            </div>
        );
    }

    const others = POSTS.filter((p) => p.slug !== slug).slice(0, 2);

    return (
        <div className="pt-24 md:pt-32 pb-24" data-testid="blog-post-page">
            <Seo title={`${post.title} — District Greenz DMV`} description={post.excerpt} path={`/blog/${post.slug}`} />
            <article className="container-x max-w-3xl">
                <Reveal>
                    <Link to="/blog" data-testid="blog-post-back" className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-sage hover:text-leaf-bright transition-colors">
                        <ArrowLeft size={13} /> The Greenz Journal
                    </Link>
                    <div className="mt-6 flex items-center gap-3">
                        <span className="rounded-full border border-leaf/40 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-leaf-bright">
                            {post.tag}
                        </span>
                        <span className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-sage">
                            <Clock size={12} /> {post.readTime} · {post.date}
                        </span>
                    </div>
                    <h1 className="mt-5 font-display text-3xl md:text-5xl font-extrabold leading-tight text-bone">
                        {post.title}
                    </h1>
                </Reveal>

                <Reveal delay={0.1}>
                    <div className="mt-8 overflow-hidden rounded-3xl border border-leaf/15">
                        <img src={post.image} alt={post.title} className="aspect-[16/8] w-full object-cover" />
                    </div>
                </Reveal>

                <Reveal delay={0.15}>
                    <div
                        className="prose-dg mt-10 text-[15px] md:text-base text-bone/80 [&_a]:text-leaf-bright [&_a]:underline [&_a]:underline-offset-4 [&_a:hover]:text-bone [&_h2]:mt-10 [&_h2]:font-display [&_h2]:text-xl [&_h2]:font-bold [&_h2]:text-bone [&_li]:mb-2"
                        dangerouslySetInnerHTML={{ __html: post.html }}
                    />
                </Reveal>

                <Reveal delay={0.2}>
                    <div className="mt-14 rounded-3xl border border-leaf/20 bg-moss/40 p-8 text-center" data-testid="blog-post-cta">
                        <p className="font-serif text-2xl md:text-3xl italic text-bone">The menu is live right now.</p>
                        <p className="mt-3 text-sm text-sage">Delivering in DC — 20007, 20011 & 20009. Open 24 hours, Monday–Sunday.</p>
                        <div className="mt-6 flex flex-wrap justify-center gap-3">
                            <Link to="/shop" data-testid="blog-post-shop-cta" className="rounded-full bg-leaf px-7 py-3.5 font-bold text-ink hover:bg-leaf-bright transition-colors">
                                Shop the Menu
                            </Link>
                            <a href="tel:+12027134325" data-testid="blog-post-call-cta" className="rounded-full border border-bone/25 px-7 py-3.5 font-semibold text-bone hover:border-leaf/50 transition-colors">
                                Call (202) 713-4325
                            </a>
                        </div>
                    </div>
                </Reveal>

                <div className="mt-16" data-testid="blog-post-related">
                    <h2 className="font-display text-2xl font-extrabold text-bone">Keep reading.</h2>
                    <div className="mt-6 grid gap-4 sm:grid-cols-2">
                        {others.map((o) => (
                            <Link
                                key={o.slug}
                                to={`/blog/${o.slug}`}
                                data-testid={`blog-related-${o.slug}`}
                                className="group rounded-2xl border border-leaf/12 bg-moss/40 p-5 transition-colors hover:border-leaf/40"
                            >
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-leaf-bright">{o.tag}</span>
                                <h3 className="mt-2 font-display font-bold leading-snug text-bone group-hover:text-leaf-bright transition-colors">
                                    {o.title}
                                </h3>
                            </Link>
                        ))}
                    </div>
                </div>
            </article>
        </div>
    );
}
